/**
 * Footer panel — copyright, contact, links, social.
 */
import {useEffect, useState} from 'react';
import type {EditorFooterLink, EditorFooterPayload} from '../../lib/types';
import {NAV_TARGET_LABELS} from '../../lib/ui-labels';

interface Props {
  initial: EditorFooterPayload;
  onSave: (next: EditorFooterPayload) => Promise<void>;
  onDirty: () => void;
  saving: boolean;
}

const EMPTY: EditorFooterPayload = {
  copyright: '',
  tagline: null,
  address: null,
  contact_email: null,
  contact_phone: null,
  links: [],
  social_links: {},
};

export default function FooterPanel({initial, onSave, onDirty, saving}: Props) {
  const [draft, setDraft] = useState<EditorFooterPayload>(initial || EMPTY);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(initial || EMPTY);
  }, [initial]);

  const set = (patch: Partial<EditorFooterPayload>) => {
    setDraft((d: EditorFooterPayload) => ({...d, ...patch}));
    onDirty();
  };

  const setSocial = (k: string, v: string) => {
    set({social_links: {...(draft.social_links || {}), [k]: v}});
  };
  const removeSocial = (k: string) => {
    const next = {...(draft.social_links || {})};
    delete next[k];
    set({social_links: next});
  };

  const addLink = () => {
    set({links: [...(draft.links || []), {label: '新链接', url: '', target: '_self'}]});
  };
  const updateLink = (i: number, patch: Partial<EditorFooterLink>) => {
    set({links: (draft.links || []).map((l: EditorFooterLink, idx: number) => (idx === i ? {...l, ...patch} : l))});
  };
  const removeLink = (i: number) => {
    set({links: (draft.links || []).filter((_: EditorFooterLink, idx: number) => idx !== i)});
  };

  const handleSave = async () => {
    setError(null);
    try {
      await onSave(draft);
    } catch (e: any) {
      setError(e?.message || '保存失败');
    }
  };

  const socialEntries = Object.entries(draft.social_links || {});

  return (
    <div className="footer-panel">
      <h2>页脚</h2>
      {error && <div className="editor-error">{error}</div>}

      <div className="field-grid">
        <label className="field">
          <span className="field-label">版权信息</span>
          <input
            type="text"
            value={draft.copyright || ''}
            onChange={(e) => set({copyright: e.target.value || null})}
            placeholder="例如：© 2026 梧炅有限公司"
          />
        </label>
        <label className="field">
          <span className="field-label">页脚副标题</span>
          <input
            type="text"
            value={draft.tagline || ''}
            onChange={(e) => set({tagline: e.target.value || null})}
            placeholder="一句简短的话"
          />
        </label>
        <label className="field">
          <span className="field-label">公司地址</span>
          <textarea
            rows={2}
            value={draft.address || ''}
            onChange={(e) => set({address: e.target.value || null})}
          />
        </label>
        <label className="field">
          <span className="field-label">联系邮箱</span>
          <input
            type="email"
            value={draft.contact_email || ''}
            onChange={(e) => set({contact_email: e.target.value || null})}
          />
        </label>
        <label className="field">
          <span className="field-label">联系电话</span>
          <input
            type="tel"
            value={draft.contact_phone || ''}
            onChange={(e) => set({contact_phone: e.target.value || null})}
          />
        </label>
      </div>

      <fieldset className="field-group">
        <legend>页脚链接</legend>
        {(draft.links || []).map((l, i) => (
          <div className="nav-item" key={i}>
            <div className="field-grid">
              <label className="field">
                <span className="field-label">显示文字</span>
                <input
                  type="text"
                  value={l.label}
                  onChange={(e) => updateLink(i, {label: e.target.value})}
                />
              </label>
              <label className="field">
                <span className="field-label">链接地址</span>
                <input
                  type="url"
                  value={l.url}
                  onChange={(e) => updateLink(i, {url: e.target.value})}
                />
              </label>
              <label className="field">
                <span className="field-label">打开方式</span>
                <select
                  value={l.target}
                  onChange={(e) => updateLink(i, {target: e.target.value as '_self' | '_blank'})}
                >
                  {Object.entries(NAV_TARGET_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </label>
            </div>
            <button
              className="icon-btn danger"
              onClick={() => removeLink(i)}
              type="button"
            >
              × 删除
            </button>
          </div>
        ))}
        <button className="btn-secondary" onClick={addLink} type="button">
          + 添加链接
        </button>
      </fieldset>

      <fieldset className="field-group">
        <legend>社交媒体</legend>
        {socialEntries.map(([k, v]) => (
          <div className="row-with-delete" key={k}>
            <span className="field-label" style={{minWidth: 90}}>{k}</span>
            <input
              type="url"
              value={v}
              onChange={(e) => setSocial(k, e.target.value)}
            />
            <button
              className="icon-btn danger"
              onClick={() => removeSocial(k)}
              type="button"
            >
              ×
            </button>
          </div>
        ))}
        <AddSocial onAdd={setSocial} />
      </fieldset>

      <div className="section-editor-actions">
        <button className="btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? '保存中…' : '保存页脚'}
        </button>
      </div>
    </div>
  );
}

function AddSocial({onAdd}: {onAdd: (k: string, v: string) => void}) {
  const [k, setK] = useState('');
  const [v, setV] = useState('');
  return (
    <div className="row-with-add">
      <input
        type="text"
        placeholder="平台名（例：微信公众号、知乎）"
        value={k}
        onChange={(e) => setK(e.target.value)}
      />
      <input
        type="url"
        placeholder="https://..."
        value={v}
        onChange={(e) => setV(e.target.value)}
      />
      <button
        className="btn-secondary"
        onClick={() => {
          if (k && v) {
            onAdd(k, v);
            setK('');
            setV('');
          }
        }}
        type="button"
      >
        + 添加
      </button>
    </div>
  );
}
